import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { brmAPI, referralAPI } from "@/services/api";
import { REFERRAL_ORIGIN } from "@/config/api";
import { motion } from "framer-motion";
import {
  setCachedDashboard,
  getCachedDashboard,
  getPendingEnrollments,
  syncPendingEnrollments
} from "@/services/offline";
import {
  Wallet,
  Clock,
  TrendingUp,
  UserPlus,
  RefreshCw,
  CheckCircle2,
  Gift,
  Copy,
  Medal,
} from "lucide-react";
import {
  TailAdminMetricCard,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";
import NavigatorPanel from "@/components/brm/NavigatorPanel";

const BRMDashboard = ({ isOnline }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [activeLinks, setActiveLinks] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, profileRes, productsRes] = await Promise.all([
        brmAPI.getDashboard(selectedProduct !== "all" ? selectedProduct : null),
        brmAPI.getProfile(),
        brmAPI.getProducts()
      ]);
      setStats(dashboardRes.data);
      setProfile(profileRes.data);
      setProducts(productsRes.data);
      setCachedDashboard(dashboardRes.data);
      // Load active campaign deep-links (non-fatal if fails)
      try {
        const linksRes = await referralAPI.getMyLinks();
        setActiveLinks((linksRes.data || []).filter(l => l.campaign_status === "active"));
      } catch {}
    } catch (error) {
      const cached = getCachedDashboard();
      if (cached) {
        setStats(cached.data);
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  const handleSync = async () => {
    if (!isOnline) {
      toast.error("You're offline. Connect to sync.");
      return;
    }

    setSyncing(true);
    try {
      const defaultProduct = products.length > 0 ? products[0].id : null;
      const result = await syncPendingEnrollments((phone, productId, notes) =>
        brmAPI.createEnrollment(phone, productId || defaultProduct, notes)
      );
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} enrollment(s)`);
        fetchData();
      }
      setPendingCount(getPendingEnrollments().length);
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const copyReferralCode = () => {
    if (profile?.referral_code) {
      navigator.clipboard.writeText(profile.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    fetchData();
    setPendingCount(getPendingEnrollments().length);
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTierStyle = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'gold': return { icon: 'bg-amber-50 text-amber-600', bar: 'bg-amber-500' };
      case 'silver': return { icon: 'bg-gray-100 text-gray-600', bar: 'bg-gray-500' };
      default: return { icon: 'bg-orange-50 text-orange-600', bar: 'bg-orange-500' };
    }
  };

  if (loading && !stats) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
      </div>
    );
  }

  const tierStyle = getTierStyle(stats?.tier);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-6 space-y-6 pb-24"
    >
      {/* Premium Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs font-bold text-brand-500 uppercase tracking-widest">Performance Dashboard</p>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
            Hi, {profile?.name?.split(' ')[0] || 'Agent'}
          </h1>
        </div>
        <Button
          variant="secondary"
          size="icon"
          onClick={fetchData}
          disabled={loading || !isOnline}
          className="rounded-full shadow-theme-xs"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Tier Mission Card */}
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`rounded-xl p-2.5 ${tierStyle.icon}`}>
                <Medal className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Current Tier</p>
                <p className="text-xl font-semibold text-gray-900">{stats?.tier || 'Bronze'}</p>
              </div>
            </div>
            {stats?.next_tier && (
              <div className="text-right">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Target: {stats.next_tier}</p>
                <div className="w-32 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${stats.tier_progress}%` }}
                    className={`h-full ${tierStyle.bar}`}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.active_users || 0}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">Total Earned</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats?.wallet_balance || 0)}</p>
            </div>
          </div>
      </section>

      {/* Product Filter */}
      {products.length > 1 && (
        <Select value={selectedProduct} onValueChange={setSelectedProduct}>
          <SelectTrigger className="w-full h-11 rounded-xl border-gray-200 bg-white shadow-theme-xs">
            <SelectValue placeholder="All Products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            {products.map((product) => (
              <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* ── Active Campaign Deep-Links ──────────────────────────────────── */}
      {activeLinks.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Active Campaign Links</p>
            <button
              className="text-[10px] text-brand-500 font-semibold"
              onClick={() => navigate("/brm/campaigns")}
            >
              View all
            </button>
          </div>
          {activeLinks.slice(0, 2).map((link) => (
            <section
              key={link.token}
              className="cursor-pointer rounded-2xl border border-brand-100 bg-brand-50 p-3 transition-shadow hover:shadow-theme-xs"
              onClick={() => navigate(`/brm/campaigns/${link.campaign_id}`)}
            >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-xs truncate">{link.campaign_title}</p>
                    <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">/r/{link.token}</p>
                    <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                      <span className="text-brand-500">{link.clicks} clicks</span>
                      <span className="text-emerald-600">{link.conversions} converted</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={copiedToken === link.token ? "default" : "outline"}
                    className="h-8 px-3 rounded-lg shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${REFERRAL_ORIGIN}/r/${link.token}`;
                      navigator.clipboard.writeText(url);
                      setCopiedToken(link.token);
                      toast.success("Deep-link copied!");
                      setTimeout(() => setCopiedToken(null), 2000);
                    }}
                  >
                    {copiedToken === link.token ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
            </section>
          ))}
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Button
          onClick={() => navigate("/brm/enroll")}
          className="h-auto py-6 flex-col gap-3 rounded-2xl bg-brand-500 hover:bg-brand-600 shadow-theme-xs"
        >
          <div className="p-2 bg-white/20 rounded-lg">
            <UserPlus className="w-6 h-6" />
          </div>
          <span className="font-bold">Enroll User</span>
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/brm/wallet")}
          className="h-auto py-6 flex-col gap-3 rounded-2xl border-gray-200 bg-white shadow-theme-xs"
        >
          <div className="p-2 bg-brand-50 rounded-lg">
            <Wallet className="w-6 h-6 text-brand-500" />
          </div>
          <span className="font-bold">Wallet</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <TailAdminMetricCard
          label="Pending"
          value={stats?.pending_users || 0}
          icon={Clock}
          tone="warning"
          helper="Awaiting verification"
        />
        <TailAdminMetricCard
          label="Monthly"
          value={formatCurrency(stats?.this_month_earnings || 0)}
          icon={TrendingUp}
          helper="This month"
        />
      </div>

      {/* Referral Card */}
      <TailAdminPanel title="Invite & Earn" description="Share your referral code with other brand representatives." action={null}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-50 shadow-theme-xs flex items-center justify-center">
              <Gift className="w-6 h-6 text-brand-500" />
            </div>
            <div>
              <p className="text-[10px] font-black text-brand-500 uppercase">Referral Code</p>
              <p className="text-lg font-semibold tracking-tight text-gray-900">{profile?.referral_code}</p>
            </div>
          </div>
          <Button size="sm" variant="secondary" onClick={copyReferralCode} className="rounded-xl">
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </TailAdminPanel>

      <NavigatorPanel />
    </motion.div>
  );
};

export default BRMDashboard;
